import { Layout } from '../components/layout/layout.tsx';
import { Toolbar } from '../components/toolbar/toolbar.tsx';
import { ToolbarTitle } from '../components/toolbar/toolbar-title.tsx';
import { SamplingTimeline } from '../content/timeline/sampling-timeline.tsx';
import { getDateRange, getWeekOffset, getWeekRange } from '../utils/date.ts';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHome, faRefresh } from '@fortawesome/free-solid-svg-icons';
import { ToolbarSeparator } from '../components/toolbar/toolbar-separator.tsx';
import { Button } from '@progress/kendo-react-buttons';
import { DropDownList, type DropDownListChangeEvent } from '@progress/kendo-react-dropdowns';
import { SamplingDateToolbarCtrl } from '../content/toolbar/sampling-date-toolbar-ctrl.tsx';
import { useEffect, useMemo, useState } from 'react';
import { TimelineResolution } from './sampling-timeline-page.types.ts';
import type { SamplerUser, SamplingJob, SamplingJobStatusRef } from '../services/api.types.ts';
import { getSamplers, getSamplingJobs, getSamplingJobStatuses } from '../services/api.ts';
import { LoaderRender } from '../components/render/loader-render.tsx';
import { ErrorRender } from '../components/render/error-render.tsx';
import styles from './sampling-timeline-page.module.scss';
import type { SamplingTimelineJob } from '../content/timeline/sampling-timeline.types.ts';
import { SamplingJobDialog } from '../dialog/sampling-job-dialog.tsx';
import type { CheckboxChangeEvent } from '@progress/kendo-react-inputs';
import { Checkbox } from '../components/input/checkbox.tsx';
import type { CalendarChangeEvent } from '@progress/kendo-react-dateinputs';

const BREADCRUMB_ITEMS = [
  {
    id: 'homepage',
    text: 'Home',
    url: '/',
    icon: <FontAwesomeIcon icon={faHome} size={'lg'} />,
  },
  {
    id: 'sampling-timeline',
    text: 'Sampling Timeline',
    url: '/sampling-timeline',
  },
];

const RESOLUTION_OPTIONS = [{ id: TimelineResolution.Weekly, title: 'Weekly Schedule' }];

export function SamplingTimelinePage() {
  const [samplerUsers, setSamplerUsers] = useState<SamplerUser[]>([]);
  const [samplingJobs, setSamplingJobs] = useState<SamplingTimelineJob[]>([]);
  const [samplingJobStatuses, setSamplingJobStatuses] = useState<SamplingJobStatusRef[]>([]);
  const [selectedDateCtrl, setSelectedDateCtrl] = useState<Date>(new Date());
  const [offsetDateCtrl, setOffsetDateCtrl] = useState<number>(0);
  const [openSamplingJob, setOpenSamplingJob] = useState<SamplingJob | null>(null);
  const [inactiveFlag, setInactiveFlag] = useState<boolean>(false);
  const [timelineResolution, setTimelineResolution] = useState<TimelineResolution>(TimelineResolution.Weekly);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');

  const dateRange = useMemo(() => {
    const weekRange = getWeekRange(offsetDateCtrl);
    return getDateRange(weekRange.start, weekRange.end);
  }, [offsetDateCtrl]);

  useEffect(() => {
    let isMounted = true;

    (async () => {
      try {
        const [statuses, samplers] = await Promise.all([
          getSamplingJobStatuses(),
          getSamplers({ showInactive: false }),
        ]);
        if (isMounted) {
          setSamplingJobStatuses(statuses);
          setSamplerUsers(samplers);
        }
      } catch (error) {
        console.error('Error loading sampling timeline:', error);
        if (isMounted) {
          setError('Unable to load sampling timeline');
        }
      }
    })();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;

    (async () => {
      setLoading(true);
      try {
        const jobs = await getSamplingJobs({
          showInactive: inactiveFlag,
          showUnassigned: false,
          startDate: dateRange[0],
          endDate: dateRange[dateRange.length - 1],
        });
        if (isMounted) {
          setSamplingJobs(
            jobs.map((job) => {
              const jobStart = new Date(job.plannedDate!);
              const jobEnd = new Date(job.plannedDate!);
              jobEnd.setDate(jobEnd.getDate() + job.estimatedDays);

              return { ...job, plannedDate: job.plannedDate!, startDate: jobStart, endDate: jobEnd };
            })
          );
        }
      } catch (error) {
        console.error('Error loading sampling timeline:', error);
        if (isMounted) {
          setError('Unable to load sampling timeline');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    })();

    return () => {
      isMounted = false;
    };
  }, [inactiveFlag, dateRange]);

  const handleResolutionChange = ({ value }: DropDownListChangeEvent) => {
    setTimelineResolution(value.id as TimelineResolution);
  };

  const handlePrevWeek = () => {
    setOffsetDateCtrl((prev) => prev - 1);
  };

  const handleNextWeek = () => {
    setOffsetDateCtrl((prev) => prev + 1);
  };

  const handleDateChange = ({ value }: CalendarChangeEvent) => {
    const offset = getWeekOffset(value);
    setOffsetDateCtrl(offset);
    setSelectedDateCtrl(value);
  };

  const handleFlagChange = ({ value }: CheckboxChangeEvent) => {
    setInactiveFlag(value);
  };

  const handleRefresh = async () => {
    setLoading(true);
    try {
      const jobs = await getSamplingJobs({
        showInactive: inactiveFlag,
        showUnassigned: false,
        startDate: dateRange[0],
        endDate: dateRange[dateRange.length - 1],
      });
      setSamplingJobs(
        jobs.map((job) => {
          const jobStart = new Date(job.plannedDate!);
          const jobEnd = new Date(job.plannedDate!);
          jobEnd.setDate(jobEnd.getDate() + job.estimatedDays);

          return { ...job, plannedDate: job.plannedDate!, startDate: jobStart, endDate: jobEnd };
        })
      );
    } catch (error) {
      console.error('Error loading sampling timeline:', error);
      setError('Unable to load sampling timeline');
    } finally {
      setLoading(false);
    }
  };

  const handleJobClick = (job: SamplingTimelineJob) => {
    setOpenSamplingJob(job as SamplingJob);
  };

  const handleDialogApply = async () => {
    try {
      const jobs = await getSamplingJobs({
        showInactive: inactiveFlag,
        showUnassigned: false,
        startDate: dateRange[0],
        endDate: dateRange[dateRange.length - 1],
      });
      setSamplingJobs(
        jobs.map((job) => {
          const jobStart = new Date(job.plannedDate!);
          const jobEnd = new Date(job.plannedDate!);
          jobEnd.setDate(jobEnd.getDate() + job.estimatedDays);

          return { ...job, plannedDate: job.plannedDate!, startDate: jobStart, endDate: jobEnd };
        })
      );
    } catch (error) {
      console.error('Error loading sampling timeline:', error);
      setError('Unable to load sampling timeline');
    } finally {
      handleDialogClose();
    }
  };

  const handleDialogClose = () => {
    setOpenSamplingJob(null);
  };

  return (
    <>
      {' '}
      <Layout breadcrumb={BREADCRUMB_ITEMS}>
        <Toolbar>
          <ToolbarTitle flex={1}>Sampling Schedule</ToolbarTitle>
          <ToolbarSeparator />
          <DropDownList
            data={RESOLUTION_OPTIONS}
            dataItemKey={'id'}
            textField={'title'}
            value={RESOLUTION_OPTIONS.find((opt) => opt.id === timelineResolution)}
            onChange={handleResolutionChange}
            style={{ width: 200 }}
          />
          <ToolbarSeparator />
          <SamplingDateToolbarCtrl
            selectedDate={selectedDateCtrl}
            weekStart={dateRange[0]}
            weekEnd={dateRange[dateRange.length - 1]}
            onPrevWeek={handlePrevWeek}
            onNextWeek={handleNextWeek}
            onChange={handleDateChange}
          />
          <ToolbarSeparator />
          <Checkbox checked={inactiveFlag} label={'Show Inactive'} onChange={handleFlagChange} />
          <ToolbarSeparator />
          <Button themeColor={'primary'} className={styles.refreshBtn} onClick={handleRefresh}>
            <FontAwesomeIcon icon={faRefresh} size={'1x'} />
          </Button>
        </Toolbar>
        {loading || error ? (
          <div className={styles.contentRender}>
            {loading ? <LoaderRender message={'Loading...'} /> : <ErrorRender message={error} />}
          </div>
        ) : (
          <div className={styles.timelineContent}>
            <SamplingTimeline
              className={styles.timeline}
              samplers={samplerUsers}
              jobs={samplingJobs}
              dateRange={dateRange}
              onJobClick={handleJobClick}
            />
          </div>
        )}
      </Layout>
      {openSamplingJob !== null && (
        <SamplingJobDialog
          job={openSamplingJob}
          jobStatuses={samplingJobStatuses}
          onApply={handleDialogApply}
          onClose={handleDialogClose}
        />
      )}
    </>
  );
}
